import { notFound, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import type { PortableTextMarkDefinition } from "@portabletext/types";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import toMarkdown from "@sanity/block-content-to-markdown";
import { parsePhoneNumber } from "awesome-phonenumber";
import { z } from "zod";
import type { Facility, RoamdocJobPost } from "#/db/schema";
import {
  getActiveRoamdocJobPostByShortId,
  countJobPostsByPrimaryCityId,
  getFacilitiesByHiringOrgSlug,
  getJobPostsByHiringOrgSlug,
  getRoamdocJobPostById,
  getActiveRoamdocJobPostsByPrimaryCityId,
} from "#/lib/query";
import { getSimilarJobsData } from "#/lib/job-postings.server";
import { getSanityClient } from "#/lib/sanity.server";
import type { PageMetadata } from "#/lib/seo";
import { taxonomiesByTag } from "#/lib/taxonomies/taxonomy_tools";
import { TAG } from "#/types/taxonomies";
import type {
  ArticleProps,
  HiringOrg,
  Image,
  JobCityProps,
  SiteSettingsProps,
  TaxonomyTag,
} from "#/types/types";

const SITE_URL = "https://www.roamdoc.com";

export type ArticleCatchAllData = {
  kind: "article";
  slug: string;
  metadata: PageMetadata;
  article: Omit<ArticleProps, "body"> & {
    body: string;
  };
  jsonLd: string;
};

export type AnesthesiologistJobsLocationCatchAllData = {
  kind: "anesthesiologist-jobs-location";
  slug: string;
  metadata: PageMetadata;
  city: JobCityProps;
};

export type JobsLocationCatchAllData = {
  kind: "jobs-location";
  slug: string;
  metadata: PageMetadata;
  jobPosts: RoamdocJobPost[];
  jobPostsCount: number;
};

export type JobsAtHiringOrgCatchAllData = {
  kind: "jobs-at-hiring-org";
  slug: string;
  metadata: PageMetadata;
  hiringOrg: HiringOrg;
  facilities: Facility[];
  jobPostings: RoamdocJobPost[];
  jsonLd: string;
};

export type JobPostingCatchAllData = {
  kind: "job-posting";
  slug: string;
  metadata: PageMetadata;
  posting: RoamdocJobPost;
  hiringOrg: HiringOrg | null;
  similarJobs: RoamdocJobPost[];
  jsonLd: string | null;
};

export type CatchAllPageData =
  | ArticleCatchAllData
  | AnesthesiologistJobsLocationCatchAllData
  | JobsLocationCatchAllData
  | JobsAtHiringOrgCatchAllData
  | JobPostingCatchAllData;

export const loadCatchAllPage = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      slug: z.string().min(1),
    }),
  )
  .handler(async ({ data }) => {
    const { slug } = data;

    if (slug.includes("job-posting-")) {
      return loadJobPostingPage(slug);
    }

    if (slug.includes("anesthesiologist-jobs-in-")) {
      return loadAnesthesiologistJobsLocationPage(
        slug.replace("anesthesiologist-jobs-in-", ""),
      );
    }

    if (slug.split("/")[0]?.includes("-jobs-in-")) {
      return loadJobsLocationPage(slug.split("/")[0]!);
    }

    if (slug.includes("jobs-at-")) {
      return loadJobsAtHiringOrgPage(slug);
    }

    return loadArticlePage(slug);
  });

async function loadArticlePage(slug: string): Promise<ArticleCatchAllData> {
  const article = await getArticle(slug);

  return {
    kind: "article",
    slug,
    metadata: {
      alternates: {
        canonical: `${SITE_URL}/${slug}`,
      },
      description: article.metaDescription ?? "Roamdoc",
      metadataBase: SITE_URL,
      openGraph: {
        images: [
          buildArticleOgImage(article, 1 / 1),
          buildArticleOgImage(article, 4 / 3),
          buildArticleOgImage(article, 16 / 9),
        ],
      },
      title: article.metaTitle ?? "Roamdoc",
    },
    article: {
      ...article,
      body: portableTextToMarkdown(article.body),
    },
    jsonLd: JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "BlogPosting",
      author: [
        {
          "@type": "Person",
          honorificPrefix: "Dr",
          name: "Frank Melo, MD",
          url: `${SITE_URL}/about-roamdoc/frank-melo-md`,
        },
      ],
      headline: article.title,
      image: [
        `${SITE_URL}${mainImageUrl(article, 1 / 1)}`,
        `${SITE_URL}${mainImageUrl(article, 4 / 3)}`,
        `${SITE_URL}${mainImageUrl(article, 16 / 9)}`,
      ],
      inLanguage: "en-US",
      url: `${SITE_URL}/${slug}`,
    }),
  };
}

async function loadAnesthesiologistJobsLocationPage(
  slug: string,
): Promise<AnesthesiologistJobsLocationCatchAllData> {
  const city = await getJobCity(slug);

  const salaryAverageText =
    city.rank === 1
      ? `Salary in ${city.state} averages ${formatAsCurrency(
          city.anesthesiologistSalary.avg,
        )}. `
      : "";

  return {
    kind: "anesthesiologist-jobs-location",
    slug,
    metadata: {
      alternates: {
        canonical: `${SITE_URL}/anesthesiologist-jobs-in-${slug}`,
      },
      description: `${salaryAverageText}Jobs in ${city.regionalCities
        .map((regionalCity) => regionalCity.name)
        .join(", ")}`,
      metadataBase: SITE_URL,
      openGraph: {
        images: [`${SITE_URL}/og-image.png`],
      },
      title: `Search Physician Anesthesiologist Jobs in ${city.name}, ${city.stateCode}`,
    },
    city,
  };
}

async function loadJobsLocationPage(
  slug: string,
): Promise<JobsLocationCatchAllData> {
  const [roleSegment, location] = parseJobsLocationSlug(slug);
  const primaryCityId = cityIdByLocationSlug[location];
  const category = getRoleCategory(roleSegment);

  if (!primaryCityId || !category) {
    throwNotFound();
  }

  const taxonomyIds = taxonomiesByTag(category).map((taxonomy) => taxonomy.id);

  const jobPosts = await getActiveRoamdocJobPostsByPrimaryCityId(
    primaryCityId,
    taxonomyIds,
    {
      with: {
        facility: true,
      },
      limit: 10,
    },
  );

  const { count } = await countJobPostsByPrimaryCityId(primaryCityId, taxonomyIds);
  const role = capitalizeWords(roleSegment.replaceAll("-", " "));
  const { cityName, stateId } = parseLocation(location);

  return {
    kind: "jobs-location",
    slug,
    metadata: {
      alternates: {
        canonical: `${SITE_URL}/${slug}`,
      },
      description:
        "Find the latest job postings for healthcare professionals near you.",
      metadataBase: SITE_URL,
      openGraph: {
        images: [`${SITE_URL}/og-image.png`],
      },
      title: `Search ${role} jobs near ${cityName}, ${stateId}`,
    },
    jobPosts,
    jobPostsCount: count,
  };
}

async function loadJobsAtHiringOrgPage(
  slug: string,
): Promise<JobsAtHiringOrgCatchAllData> {
  const hiringOrg = await getHiringOrgBySlug(slug);
  const hiringOrgSlug = slug.split("jobs-at-")[1] ?? "";
  const facilities = await getFacilitiesByHiringOrgSlug(hiringOrgSlug);
  const jobPostings = await getJobPostsByHiringOrgSlug(hiringOrgSlug, {
    with: {
      facility: true,
    },
    limit: 10,
  });
  const ogImage = getImage(hiringOrg, "OG_IMAGE");

  return {
    kind: "jobs-at-hiring-org",
    slug,
    metadata: {
      alternates: {
        canonical: `${SITE_URL}/${slug}`,
      },
      description: hiringOrg.metaDescription ?? "",
      metadataBase: SITE_URL,
      openGraph: {
        images: [ogImage?.url ?? `${SITE_URL}/og-image.png`],
      },
      title: hiringOrg.metaTitle ?? hiringOrg.name,
    },
    facilities,
    hiringOrg,
    jobPostings,
    jsonLd: JSON.stringify(buildHiringOrgJsonLd(hiringOrg, facilities)),
  };
}

async function loadJobPostingPage(
  slug: string,
): Promise<JobPostingCatchAllData> {
  const posting = await getCanonicalJobPosting(slug);
  const hiringOrg = posting.hiringOrgId
    ? await getHiringOrgById(posting.hiringOrgId)
    : null;
  const similarJobs =
    posting.parentCityId === null
      ? []
      : await getSimilarJobsData(
          posting.parentCityId,
          posting.primaryRoleTaxonomyId,
          posting.id,
        );

  const ogImage =
    (hiringOrg && (getImage(hiringOrg, "OG_IMAGE") ?? getImage(hiringOrg, "COVER")))
      ?.url ?? `${SITE_URL}/og-image.png`;

  return {
    kind: "job-posting",
    slug,
    metadata: {
      alternates: {
        canonical: `${SITE_URL}/${slug}`,
      },
      description: posting.metaDescription ?? posting.description?.slice(0, 140),
      metadataBase: SITE_URL,
      openGraph: {
        images: [ogImage],
      },
      title: posting.metaTitle ?? posting.title ?? "Roamdoc Job Posting",
    },
    hiringOrg,
    jsonLd: posting.schema ? JSON.stringify(posting.schema) : null,
    posting,
    similarJobs,
  };
}

async function getArticle(slug: string): Promise<ArticleProps> {
  const client = getSanityClient();
  const query = `
    *[_type == 'article' && slug == $slug]{
      ...,
      body[]{
        ...,
        markDefs[]{
          ...,
          _type == "internalLink" => {
            "slug": @.reference->slug
          }
        }
      },
      "mainImage": {
        "assetId": mainImage.asset->_ref,
        "url": "/free-stock-images/" + mainImage.filename,
        "alt": mainImage.alt,
        "caption": mainImage.caption,
        "width": mainImage.asset->metadata.dimensions.width,
        "height": mainImage.asset->metadata.dimensions.height
      },
    }
  `;

  const articles = await client.fetch<ArticleProps[]>(query, { slug });
  const article = articles[0];

  if (article) {
    return article;
  }

  const siteSettings = await getSiteSettings();
  const redirection = siteSettings.redirects.find(
    (candidate) => candidate.sourcePath === slug,
  );

  if (redirection) {
    throwRedirect(redirection.target);
  }

  throwNotFound();
}

async function getSiteSettings(): Promise<SiteSettingsProps> {
  const client = getSanityClient();
  const query = `
    *[_type == 'siteSettings']{
      ...
    }
  `;

  const settings = await client.fetch<SiteSettingsProps[]>(query);
  const siteSettings = settings[0];

  if (!siteSettings) {
    throw new Error("Site settings not found");
  }

  return siteSettings;
}

async function getJobCity(slug: string): Promise<JobCityProps> {
  const client = getSanityClient();
  const query = `
    *[_type == 'jobCity' && slug == $slug]{
      ...,
      'stateCities': *[_type == ^._type && state == ^.state && slug != ^.slug] {
        name,
        stateCode,
        slug,
        description,
        rank,
        salary
      } | order(rank asc),
      stateLink-> {
        slug,
        state,
        description
      }
    }
  `;

  const cities = await client.fetch<JobCityProps[]>(query, { slug });
  const city = cities[0];

  if (!city) {
    throwNotFound();
  }

  return city;
}

async function getHiringOrgById(hiringOrgId: string): Promise<HiringOrg | null> {
  const client = getSanityClient();
  const query = `
    *[_type == 'hiringOrg' && _id == $hiringOrgId]{
      ...,
      "images": images[] {
        "assetId": asset->_ref,
        "url": "${SITE_URL}/healthcare-job-search/images/" + filename,
        "alt": alt,
        "caption": caption,
        "width": asset->metadata.dimensions.width,
        "height": asset->metadata.dimensions.height,
        imageType,
      },
    }
  `;

  const hiringOrgs = await client.fetch<HiringOrg[]>(query, { hiringOrgId });
  return hiringOrgs[0] ?? null;
}

async function getHiringOrgBySlug(slug: string): Promise<HiringOrg> {
  const hiringOrgSlug = slug.split("jobs-at-")[1];

  if (!hiringOrgSlug) {
    throwNotFound();
  }

  const client = getSanityClient();
  const query = `
    *[_type == 'hiringOrg' && slug == $hiringOrgSlug]{
      ...,
      "images": images[] {
        "assetId": asset->_ref,
        "url": "${SITE_URL}/healthcare-job-search/images/" + filename,
        "alt": alt,
        "caption": caption,
        "width": asset->metadata.dimensions.width,
        "height": asset->metadata.dimensions.height,
        imageType,
      },
    }
  `;

  const hiringOrgs = await client.fetch<HiringOrg[]>(query, { hiringOrgSlug });
  const hiringOrg = hiringOrgs[0];

  if (!hiringOrg) {
    throwNotFound();
  }

  return hiringOrg;
}

async function getCanonicalJobPosting(slug: string): Promise<RoamdocJobPost> {
  const parts = slug.split("job-posting-");
  const cleanedFirstPart = parts[0]?.endsWith("-")
    ? parts[0].slice(0, -1)
    : parts[0];
  const shortId = parts[parts.length - 1] ?? "";
  const redirectSlug = slug.split("/")[0] ?? "/";

  const posting = await getActiveRoamdocJobPostByShortId(shortId, {
    with: {
      facility: true,
      mirror: true,
    },
  });

  if (!posting) {
    throwRedirect(`/${redirectSlug}`);
  }

  if (posting.slug !== cleanedFirstPart) {
    const addHyphen = posting.slug?.endsWith("/") ? "" : "-";

    throwRedirect(`/${posting.slug}${addHyphen}job-posting-${shortId}`);
  }

  if (posting.canonicalId && posting.canonicalId !== posting.id) {
    const canonicalPost = await getRoamdocJobPostById(posting.canonicalId);

    if (!canonicalPost) {
      throwRedirect(`/${redirectSlug}`);
    }

    const addHyphen = canonicalPost.slug?.endsWith("/") ? "" : "-";

    throwRedirect(
      `/${canonicalPost.slug}${addHyphen}job-posting-${canonicalPost.shortId}`,
    );
  }

  return posting;
}

function buildArticleOgImage(article: ArticleProps, aspectRatio: number) {
  return {
    alt: article.mainImage.alt,
    height: Math.floor(article.mainImage.width / aspectRatio),
    url: `${SITE_URL}${mainImageUrl(article, aspectRatio)}`,
    width: article.mainImage.width,
  };
}

function mainImageUrl(article: ArticleProps, aspectRatio: number = 16 / 9) {
  const width = article.mainImage.width;
  const height = Math.floor(width / aspectRatio);

  if (article.mainImage.url) {
    return `${article.mainImage.url}?w=${width}&h=${height}&fit=crop`;
  }

  return "/og-image.png";
}

function buildHiringOrgJsonLd(hiringOrg: HiringOrg, facilities: Facility[]) {
  const logoImage = getImage(hiringOrg, "LOGO");

  const subOrganizations = facilities.map((facility) => ({
    "@type": "Hospital",
    address: {
      "@type": "PostalAddress",
      addressCountry: "USA",
      addressLocality: facility.cityName,
      addressRegion: facility.stateId,
      postalCode: facility.postalCode,
      streetAddress: facility.streetAddress,
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: parsePhoneNumber(facility.phone ?? "", {
        regionCode: "US",
      }).number?.international,
    },
    name: facility.name,
  }));

  return {
    "@context": "http://schema.org",
    "@type": "WebPage",
    description: hiringOrg.metaDescription,
    mainEntity: {
      "@type": "Organization",
      address: {
        "@type": "PostalAddress",
        addressCountry: "USA",
        addressLocality: hiringOrg.contact.cityName,
        addressRegion: hiringOrg.contact.stateId,
        postalCode: hiringOrg.contact.postalCode,
        streetAddress: hiringOrg.contact.streetAddress,
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: parsePhoneNumber(hiringOrg.contact.phone, {
          regionCode: "US",
        }).number?.international,
      },
      description: "",
      logo: logoImage?.url ?? "",
      name: hiringOrg.name,
      subOrganization: subOrganizations,
      url: hiringOrg.contact.url,
    },
    name: hiringOrg.metaTitle ?? hiringOrg.name,
    url: `${SITE_URL}/jobs-at-${hiringOrg.slug}`,
  };
}

function parseJobsLocationSlug(slug: string) {
  return (slug.split("-jobs-in-") as [string, string]) ?? ["", ""];
}

function parseLocation(slug: string) {
  const parts = slug.split("-");
  const stateId = parts.pop()?.toUpperCase() ?? "";
  const cityName = parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return { cityName, stateId };
}

function capitalizeWords(input: string) {
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getImage(hiringOrg: HiringOrg, imageType: Image["imageType"]) {
  return hiringOrg.images.find((image) => image.imageType === imageType) ?? null;
}

function formatAsCurrency(number: number, currencyCode = "USD") {
  return number.toLocaleString("en-US", {
    currency: currencyCode,
    minimumFractionDigits: 0,
    style: "currency",
  });
}

const cityIdByLocationSlug: Record<string, number> = {
  "austin-tx": 1271,
  "dallas-tx": 1269,
  "el-paso-tx": 1273,
  "houston-tx": 1268,
  "lubbock-tx": 1279,
  "miami-fl": 2541,
  "new-york-city-ny": 2541,
  "phoenix-az": 2539,
  "san-antonio-tx": 1270,
};

const roleCategoryBySlug = {
  nurse: TAG.nurse,
  physician: TAG.physician,
} as const;

function getRoleCategory(roleSegment: string): TaxonomyTag | undefined {
  if (roleSegment === "nurse") {
    return roleCategoryBySlug.nurse as TaxonomyTag;
  }

  if (roleSegment === "physician") {
    return roleCategoryBySlug.physician as TaxonomyTag;
  }

  return undefined;
}

function throwRedirect(href: string): never {
  // TanStack redirects are thrown control-flow responses.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({
    href,
    statusCode: 308,
  });
}

function throwNotFound(): never {
  // TanStack not-found responses are thrown control-flow responses.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw notFound();
}

function portableTextToMarkdown(body: ArticleProps["body"]) {
  const serializers = {
    marks: {
      internalLink: ({
        mark,
        children,
      }: {
        mark: PortableTextMarkDefinition;
        children: string;
      }) => {
        const { slug = "" } = mark;
        return `[${children}](/${slug as string})`;
      },
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  return toMarkdown(body, { serializers }) as string;
}
